"use strict";
// Copyright © Veeam Software Group GmbH
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDatetimeSelectorViewModel = exports.validateNotNullSeconds = exports.validateNotNullMinutes = exports.validateNotNullHours = exports.convert12To24HourFormat = void 0;
const react_1 = require("react");
const getDateWithOffset_1 = require("../helpers/getDateWithOffset");
const DatetimeInputContext_1 = require("../context/DatetimeInputContext");
const getInitialShownMonth = (value = new Date()) => {
    return {
        month: value.getMonth(),
        year: value.getFullYear()
    };
};
const convert12To24HourFormat = (hours, isAM) => {
    if (hours === 12) {
        return isAM ? 0 : 12;
    }
    return isAM ? hours : hours + 12;
};
exports.convert12To24HourFormat = convert12To24HourFormat;
const validateNotNullHours = (hours, is12Hours) => {
    if (is12Hours && hours < 1) {
        return false;
    }
    if (!is12Hours && hours < 0) {
        return false;
    }
    return is12Hours ? hours <= 12 : hours <= 23;
};
exports.validateNotNullHours = validateNotNullHours;
const validateNotNullMinutes = (minutes) => {
    return minutes >= 0 && minutes <= 59;
};
exports.validateNotNullMinutes = validateNotNullMinutes;
const validateNotNullSeconds = (seconds) => {
    return seconds >= 0 && seconds <= 59;
};
exports.validateNotNullSeconds = validateNotNullSeconds;
const useDatetimeSelectorViewModel = ({ value, startDatetime = new Date(0), endDatetime = new Date(9999, 11, 31), isDisabledDate, withTime = false, withSeconds = false, is12Hours = false, manualCloseMode, onSelect, onRequestClose, onDateChange }) => {
    const context = (0, react_1.useContext)(DatetimeInputContext_1.DatetimeInputContext);
    const now = (context === null || context === void 0 ? void 0 : context.getNowTime)
        ? context === null || context === void 0 ? void 0 : context.getNowTime()
        : (0, getDateWithOffset_1.getDateWithOffset)(new Date(), context.serverUtcOffset);
    const initialDraftHours = (0, react_1.useMemo)(() => {
        if (value && is12Hours && value.getHours() === 0) {
            return 12;
        }
        if (value && is12Hours && value.getHours() > 12) {
            return value.getHours() - 12;
        }
        return value ? value.getHours() : null;
    }, [is12Hours, value]);
    const [shownMonth, setShownMonth] = (0, react_1.useState)(getInitialShownMonth(value || now));
    const [monthSelectorValue, setMonthSelectorValue] = (0, react_1.useState)(shownMonth);
    const [monthSelectMode, setMonthSelectMode] = (0, react_1.useState)(false);
    const [draftValue, setDraftValue] = (0, react_1.useState)(value || undefined);
    const [draftHours, setDraftHours] = (0, react_1.useState)(initialDraftHours);
    const [draftMinutes, setDraftMinutes] = (0, react_1.useState)(value ? value.getMinutes() : null);
    const [draftSeconds, setDraftSeconds] = (0, react_1.useState)(withSeconds && value ? value.getSeconds() : null);
    const [draftIsAmTimePeriod, setDraftIsAmTimePeriod] = (0, react_1.useState)(!(value && value.getHours() >= 12));
    const [errorState, setErrorState] = (0, react_1.useState)({
        dateValue: false,
        hours: false,
        minutes: false,
        seconds: false
    });
    const canApplyShownMonth = (0, react_1.useMemo)(() => !!monthSelectorValue.year && typeof monthSelectorValue.month === 'number', [monthSelectorValue.month, monthSelectorValue.year]);
    const deactivate = (0, react_1.useCallback)(() => {
        onRequestClose === null || onRequestClose === void 0 ? void 0 : onRequestClose();
    }, [onRequestClose]);
    const onSelectDay = (0, react_1.useCallback)((day) => {
        setShownMonth({ year: day.getFullYear(), month: day.getMonth() });
        setErrorState(Object.assign(Object.assign({}, errorState), { dateValue: false }));
        onDateChange === null || onDateChange === void 0 ? void 0 : onDateChange(day);
        if (withTime || manualCloseMode) {
            setDraftValue(day);
            return;
        }
        onSelect === null || onSelect === void 0 ? void 0 : onSelect(day);
        deactivate();
    }, [deactivate, errorState, manualCloseMode, onDateChange, onSelect, withTime]);
    const onApplyMonthSelector = (0, react_1.useCallback)(() => {
        if (typeof monthSelectorValue.month === 'number' &&
            typeof monthSelectorValue.year === 'number') {
            const shownMonth = {
                year: monthSelectorValue.year,
                month: monthSelectorValue.month
            };
            setShownMonth(shownMonth);
            setMonthSelectMode(false);
        }
    }, [monthSelectorValue.month, monthSelectorValue.year]);
    const onCancelMonthSelector = (0, react_1.useCallback)(() => {
        setMonthSelectMode(false);
    }, []);
    const onRequestMonthSelector = (0, react_1.useCallback)(() => {
        setMonthSelectMode(true);
        setErrorState(Object.assign(Object.assign({}, errorState), { dateValue: false }));
    }, [errorState]);
    const isMonthDisabled = (0, react_1.useCallback)(({ year, month }) => {
        const lessThanStartDatetime = startDatetime
            ? new Date(year, month, 31, 23, 59, 59) < startDatetime
            : false;
        const moreThanEndDatetime = endDatetime
            ? new Date(year, month, 1, 0, 0, 0) > endDatetime
            : false;
        if (lessThanStartDatetime || moreThanEndDatetime) {
            return true;
        }
        if (!isDisabledDate) {
            return false;
        }
        const countMonthDays = new Date(year, month + 1, 0).getDate();
        for (let day = 1; day <= countMonthDays; day++) {
            if (!isDisabledDate(new Date(year, month, day))) {
                return false;
            }
        }
        return true;
    }, [endDatetime, isDisabledDate, startDatetime]);
    const isYearDisabled = (0, react_1.useCallback)((year) => {
        const lessThanStartDatetime = startDatetime
            ? new Date(year, 11, 31, 23, 59, 59) < startDatetime
            : false;
        const moreThanEndDatetime = endDatetime
            ? new Date(year, 0, 1, 0, 0, 0) > endDatetime
            : false;
        if (lessThanStartDatetime || moreThanEndDatetime) {
            return true;
        }
        if (!isDisabledDate) {
            return false;
        }
        for (let month = 0; month <= 11; month++) {
            if (!isMonthDisabled({ year, month })) {
                return false;
            }
        }
        return true;
    }, [endDatetime, isDisabledDate, isMonthDisabled, startDatetime]);
    const onMonthSelectorSelectValue = (0, react_1.useCallback)((newValue) => {
        let newMonth;
        let newYear;
        if (newValue.month === undefined) {
            newMonth = monthSelectorValue.month;
        }
        if (newValue.month || newValue.month === 0) {
            newMonth = newValue.month;
        }
        if (newValue.year === undefined) {
            newYear = monthSelectorValue.year;
        }
        if (newValue.year) {
            newYear = newValue.year;
        }
        const result = {
            month: newMonth,
            year: newYear
        };
        if (result.year &&
            typeof monthSelectorValue.month === 'number' &&
            isMonthDisabled({ month: monthSelectorValue.month, year: result.year })) {
            result.month = undefined;
        }
        setMonthSelectorValue(result);
    }, [isMonthDisabled, monthSelectorValue.month, monthSelectorValue.year]);
    const onHoursChange = (0, react_1.useCallback)((hours) => {
        setDraftHours(hours);
        setErrorState(Object.assign(Object.assign({}, errorState), { hours: false }));
    }, [errorState]);
    const onMinutesChange = (0, react_1.useCallback)((minutes) => {
        setDraftMinutes(minutes);
        setErrorState(Object.assign(Object.assign({}, errorState), { minutes: false }));
    }, [errorState]);
    const onSecondsChange = (0, react_1.useCallback)((seconds) => {
        setDraftSeconds(seconds);
        setErrorState(Object.assign(Object.assign({}, errorState), { seconds: false }));
    }, [errorState]);
    const onTimePeriodChange = (0, react_1.useCallback)((isAmTimePeriod) => {
        setDraftIsAmTimePeriod(isAmTimePeriod);
    }, []);
    const onApply = (0, react_1.useCallback)(() => {
        const isValidHours = draftHours !== null && (0, exports.validateNotNullHours)(draftHours, is12Hours);
        const isValidMinutes = draftMinutes !== null && (0, exports.validateNotNullMinutes)(draftMinutes);
        const isValidSeconds = withSeconds
            ? draftSeconds !== null && (0, exports.validateNotNullSeconds)(draftSeconds)
            : true;
        setErrorState({
            dateValue: !draftValue,
            hours: !isValidHours,
            minutes: !isValidMinutes,
            seconds: !isValidSeconds
        });
        if (onSelect &&
            draftValue &&
            withTime &&
            isValidHours &&
            isValidMinutes &&
            isValidSeconds) {
            const newDate = new Date(draftValue.getFullYear(), draftValue.getMonth(), draftValue.getDate(), is12Hours ? (0, exports.convert12To24HourFormat)(draftHours, draftIsAmTimePeriod) : draftHours, draftMinutes, draftSeconds || 0);
            onSelect(newDate);
            onRequestClose === null || onRequestClose === void 0 ? void 0 : onRequestClose();
            return;
        }
        if (onSelect && draftValue && !withTime) {
            onSelect(draftValue);
            onRequestClose === null || onRequestClose === void 0 ? void 0 : onRequestClose();
            return;
        }
    }, [
        draftHours,
        draftIsAmTimePeriod,
        draftMinutes,
        draftSeconds,
        draftValue,
        is12Hours,
        onRequestClose,
        onSelect,
        withSeconds,
        withTime
    ]);
    const isTodayDisabled = (0, react_1.useCallback)(() => {
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();
        const currentDay = now.getDate();
        const ceilDate = new Date(currentYear, currentMonth, currentDay, 23, 59, 59);
        const floorDate = new Date(currentYear, currentMonth, currentDay, 0, 0, 0);
        const lessThanStartDatetime = ceilDate < startDatetime;
        const moreThanEndDatetime = floorDate > endDatetime;
        return lessThanStartDatetime || (isDisabledDate === null || isDisabledDate === void 0 ? void 0 : isDisabledDate(now)) || moreThanEndDatetime;
    }, [endDatetime, isDisabledDate, now, startDatetime]);
    const onTodaySelect = (0, react_1.useCallback)(() => {
        const today = now;
        today.setHours(0);
        today.setMinutes(0);
        today.setSeconds(0);
        onSelectDay(today);
    }, [now, onSelectDay]);
    return {
        shownMonth,
        monthSelectorValue,
        monthSelectMode,
        canApplyShownMonth,
        isYearDisabled,
        isMonthDisabled,
        draftValue,
        draftIsAmTimePeriod,
        draftHours,
        draftMinutes,
        draftSeconds,
        is12Hours,
        onSelectDay,
        onRequestMonthSelector,
        onApplyMonthSelector,
        onCancelMonthSelector,
        onMonthSelectorSelectValue,
        onHoursChange,
        onMinutesChange,
        onSecondsChange,
        onTimePeriodChange,
        onApply,
        deactivate,
        errorState,
        isTodayDisabled,
        onTodaySelect
    };
};
exports.useDatetimeSelectorViewModel = useDatetimeSelectorViewModel;
//# sourceMappingURL=useDatetimeSelectorViewModel.js.map